import { Component, Injector, OnInit, inject } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { UserService } from '../../services/user/user.service';
import { ServiceService } from '../../services/service/service.service';
import { Service } from '../../models/service';

@Component({
    selector: 'app-company-subscription-required',
    templateUrl: './company-subscription-required.component.html',
    styleUrls: ['./company-subscription-required.component.scss']
})
export class CompanySubscriptionRequiredComponent extends BaseComponent implements OnInit {

    companyId: string = '';
    companyStatus: string | null = null;
    /** True when we should show "select a subscription" (no active subscription). */
    showChooseSubscription = false;
    showStripeCheckout = false;

    services: Service[] = [];
    servicesSelected: Service[] = [];
    billingPeriod: 'monthly' | 'yearly' = 'monthly';
    tariffTiers: { range: string; rate: string }[] = [];
    private serviceService = inject(ServiceService);
    private userService = inject(UserService);

    constructor(public serviceInjector: Injector) {
        super(serviceInjector);
    }

    ngOnInit() {
        this.companyId = (this.utils.getJwtCompanyId() || localStorage.getItem('wisper_companyId')) ?? '';
        this.companyStatus = this.utils.getJwtCompanyStatus();

        const hasSubscription = this.utils.hasActiveSubscription();

        if (!hasSubscription) {
            this.showChooseSubscription = true;
            this.loadSubscriptionPlans();
        } else {
            this.navigate.navigate(this.utils.getCompanyManagerHomeRoute());
        }
    }

    private loadSubscriptionPlans() {
        this.serviceService.getAllServices().then((services: Service[]) => {
            this.services = services;
        });
        this.tariffTiers = [
            { range: 'da 1 a 150 iscrizioni anno', rate: '2,2 % del transato' },
            { range: 'da 151 a 280 iscrizioni anno', rate: '2,0 % del transato' },
            { range: 'da 281 a 480 iscrizioni anno', rate: '1,8 % del transato' },
            { range: 'Iscrizioni illimitate', rate: '1,6 % del transato' }
        ];
    }

    getDisplayedMonthlyPrice(service: Service): number {
        const base = this.parsePrice(service.price);
        if (this.billingPeriod === 'monthly') return base;
        return this.parsePrice(service.annualPrice);
    }

    getAnnualBilledMonthlyPrice(service: Service): number {
        const periodTotal = this.parsePrice(service.annualPrice);
        const recurrencyMonths = this.getServiceRecurrencyMonths(service);
        return Number.isNaN(periodTotal) ? 0 : periodTotal / recurrencyMonths;
    }

    getServiceRecurrencyMonths(service: Service): number {
        const raw = Number(service.recurrency);
        return Number.isFinite(raw) && raw > 0 ? raw : 12;
    }

    getAnnualSavingsPercent(service: Service): number | null {
        const monthly = this.parsePrice(service.price);
        const periodTotal = this.parsePrice(service.annualPrice);
        if (Number.isNaN(monthly) || Number.isNaN(periodTotal) || monthly <= 0) {
            return null;
        }
        const fullPeriodAtMonthly = monthly * this.getServiceRecurrencyMonths(service);
        if (fullPeriodAtMonthly <= 0) {
            return null;
        }
        const pct = ((fullPeriodAtMonthly - periodTotal) / fullPeriodAtMonthly) * 100;
        if (pct <= 0.05) {
            return null;
        }
        return Math.round(pct * 10) / 10;
    }

    private parsePrice(raw: string | undefined | null): number {
        const n = parseFloat(String(raw ?? '').replace(/\s/g, '').replace(',', '.'));
        return Number.isNaN(n) ? NaN : n;
    }

    selectPlan(service: Service) {
        const alreadySelected = this.setCheckbox(service);
        this.servicesSelected = alreadySelected ? [] : [service];
    }

    setCheckbox(serviceCheckbox: Service): boolean {
        return !!this.servicesSelected.find((s: Service) => serviceCheckbox.id === s.id);
    }

    getFeatureLabel(feature: string | { name?: string; code?: string } | unknown): string {
        if (feature == null) return '';
        if (typeof feature === 'string') return feature;
        const o = feature as { name?: string; code?: string };
        return o?.name ?? o?.code ?? String(feature);
    }

    goToPayment() {
        if (this.servicesSelected.length === 0) return;
        this.showStripeCheckout = true;
    }

    backToPlanSelection() {
        this.showStripeCheckout = false;
    }

    onSubscriptionPaymentCompleted() {
        this.showChooseSubscription = false;
        this.showStripeCheckout = false;
        this.navigate.navigate(['/thankyou']);
        setTimeout(() => {
            this.userService.refreshJwtFromRefreshToken(true);
        }, 5000);
    }
}
